import {Component, Input, OnInit} from '@angular/core';
import {MapsManagerVersion} from '../../../shared/models/mapsManagerVersion.model';
import {MapsManagerVersionService} from '../../../core/data-services/mapsManagerVersion.service';
import {Product} from '../../../shared/models/product.model';
import {MindMapVersionNodeService} from '../../../core/data-services/mindMapVersionNode.service';
import {MindMapVersionNode} from '../../../shared/models/mindMapVersionNode.model';
import {forkJoin, Observable} from 'rxjs';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'emap-product-manager',
  templateUrl: './product-manager.component.html',
  styleUrls: ['./product-manager.component.scss']
})
export class ProductManagerComponent implements OnInit {

  @Input() mapsManagerVersion: MapsManagerVersion;
  @Input() mapName: string;

  public productsOnMap: Array<Product> = [];
  public productsNotOnMap: Array<Product> = [];
  public ignoredProducts: Array<Product> = [];
  public productsToAddToMap: Array<Product> = [];
  public filteredNewProducts: Array<Product> = [];
  public filteredIgnoredProducts: Array<Product> = [];
  private allProducts: Array<Product> = [];
  public productsLoading = false;
  public showLoadingMessage = false;

  constructor(
      private mapsManagerVersionService: MapsManagerVersionService,
      private mindMapVersionNodeService: MindMapVersionNodeService,
      private modal: NgbActiveModal
  ) { }

  ngOnInit(): void {
    const mapData$ = this.mindMapVersionNodeService.getAllNoPagination(this.mapsManagerVersion.id);
    const productData$ = this.mapsManagerVersionService.getRelatedProducts(this.mapsManagerVersion.id);

    this.productsLoading = true;
    forkJoin([mapData$, productData$]).subscribe(results => {
      this.setMapNodes(results[0]);
      this.setProducts(results[1]);
    }, () => {}, () => {
      this.productsLoading = false;
      this.showLoadingMessage = false;
    });

    // If it takes longer than 10 seconds to load the products, show a message to reassure the user
    setTimeout(() => {
      if (this.productsLoading) {
        this.showLoadingMessage = true;
      }
    }, 10000);
  }

  public save() {
    this.modal.close({
      addedProducts: this.productsToAddToMap.map(p => p.edmGUID),
      ignoredProducts: this.ignoredProducts.map(p => p.edmGUID),
    });
  }

  public close() {
    this.modal.close();
  }

  public addProduct(product: Product): void {
    this.productsToAddToMap = [...this.productsToAddToMap, product];
    this.productsNotOnMap.splice(this.productsNotOnMap.indexOf(product), 1);
    this.ignoredProducts.splice(this.ignoredProducts.indexOf(product), 1);
  }

  public removeProduct(product: Product): void {
    this.productsNotOnMap = [...this.productsNotOnMap, product];
    this.productsToAddToMap.splice(this.productsToAddToMap.indexOf(product), 1);
  }

  public ignoreProduct(p: Product): void {
    this.ignoredProducts = [...this.ignoredProducts, p];
    this.productsNotOnMap.splice(this.productsNotOnMap.indexOf(p), 1);
  }

  public productNameCaseInsensitiveOrder(p1: Product, p2: Product): number {
    return p1.name.localeCompare(p2.name);
  }

  public searchProductList(term: string) {
    if (!term) {
      this.filteredNewProducts = this.productsNotOnMap;
      this.filteredIgnoredProducts = this.ignoredProducts;
    } else {
      this.filteredNewProducts = this.productsNotOnMap.filter(p => p.name.toUpperCase().includes(term.toUpperCase()));
      this.filteredIgnoredProducts = this.ignoredProducts.filter(p => p.name.toUpperCase().includes(term.toUpperCase()));
    }
  }

  private setMapNodes(nodes: Array<MindMapVersionNode>) {
    this.mapsManagerVersion.mindMapVersionNodes = nodes.map(n => new MindMapVersionNode(n));
    this.setProductsOnMap();
  }

  private setProducts(products: Array<Product>) {
    this.allProducts = products.map(p => new Product(p));

    this.productsNotOnMap = this.allProducts.filter(p => p.mapStatuses &&
        p.mapStatuses[this.mapsManagerVersion.mapsManagerId] !== 'IGNORED' &&
        !this.productsOnMap.some(p1 => p1.id === p.id));

    this.ignoredProducts = this.allProducts.filter(p => p.mapStatuses &&
        p.mapStatuses[this.mapsManagerVersion.mapsManagerId] === 'IGNORED' &&
        !this.productsOnMap.some(p1 => p1.id === p.id));

    this.filteredNewProducts = this.productsNotOnMap;

    this.filteredIgnoredProducts = this.ignoredProducts;
  }

  private setProductsOnMap() {
    const productArr = [];
    const productIdSet = new Set<number>();
    for (const mindMapVersionNode of this.mapsManagerVersion.mindMapVersionNodes) {
      for (const productCompanyInstance of mindMapVersionNode.productCompanyInstances) {
        productCompanyInstance.products.forEach(product => {
          if (!productIdSet.has(product.id)) {
            productArr.push(product);
            productIdSet.add(product.id);
          }
        });
      }
    }
    this.productsOnMap = productArr;
  }

}
